import React, { useEffect, useState } from "react"
import { Link, graphql, useStaticQuery } from "gatsby"
import styled from "styled-components"

export const Nav = ({ menuLinks }) => {
  const data = useStaticQuery(graphql`
    query {
      logo: file(relativePath: { eq: "blog-logo.png" }) {
        publicURL
      }
    }
  `)
  const [open, setOpen] = useState(false)
  const [dark, setDark] = useState(false)

  useEffect(() => {
    setDark(document.documentElement.getAttribute("data-theme") === "dark")
  }, [])

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : ""
    return () => {
      document.body.style.overflow = ""
    }
  }, [open])

  const toggleTheme = () => {
    const next = !dark
    setDark(next)
    document.documentElement.setAttribute("data-theme", next ? "dark" : "light")
    try {
      localStorage.setItem("theme", next ? "dark" : "light")
    } catch (e) {
      /* ignore */
    }
  }

  return (
    <NavWrapper>
      <Brand to="/" aria-label="El Hamzi hem">
        <img src={data.logo.publicURL} alt="" width={44} height={44} />
      </Brand>
      <NavMenu $open={open} aria-label="Huvudmeny">
        <ul>
          {menuLinks.map(item => (
            <li key={item.name}>
              <Link to={item.link} onClick={() => setOpen(false)}>
                {item.name}
              </Link>
            </li>
          ))}
        </ul>
      </NavMenu>
      <Controls>
        <ThemeButton type="button" onClick={toggleTheme} aria-label={dark ? "Ljust läge" : "Mörkt läge"}>
          {dark ? "Ljust" : "Mörkt"}
        </ThemeButton>
        <NavIcon
          type="button"
          $open={open}
          aria-expanded={open}
          aria-label={open ? "Stäng meny" : "Öppna meny"}
          onClick={() => setOpen(v => !v)}
        >
          <span />
          <span />
          <span />
        </NavIcon>
      </Controls>
      {open ? <Scrim onClick={() => setOpen(false)} /> : null}
    </NavWrapper>
  )
}

const NavWrapper = styled.nav`
  position: sticky;
  top: 0;
  z-index: 40;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: var(--header-h);
  width: min(80rem, 100%);
  margin: 0 auto;
  padding: 0 1.5rem;
  background: color-mix(in srgb, var(--bg) 88%, transparent);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid var(--border);
  grid-column: 1 / -1;
  grid-row: 1 / 2;
`

const Brand = styled(Link)`
  display: inline-flex;
  align-items: center;
  color: var(--fg);

  img {
    width: 2.75rem;
    height: 2.75rem;
    object-fit: cover;
    border-radius: 10px;
  }
`

const NavMenu = styled.nav`
  position: fixed;
  top: var(--header-h);
  right: 0;
  z-index: 41;
  width: min(20rem, 88vw);
  height: calc(100dvh - var(--header-h));
  padding: 1.5rem;
  background: var(--elevated);
  border-left: 1px solid var(--border);
  transform: ${props => (props.$open ? "translateX(0)" : "translateX(110%)")};
  transition: transform 250ms cubic-bezier(0.22, 1, 0.36, 1);

  ul {
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  a {
    display: block;
    padding: 0.85rem 0.75rem;
    color: var(--fg);
    text-decoration: none;
    font-size: 1.05rem;
    border-radius: var(--radius-md);
  }

  @media ${props => props.theme.breakpoints.tablet} {
    position: static;
    transform: none;
    width: auto;
    height: auto;
    padding: 0;
    background: transparent;
    border: 0;

    ul {
      flex-direction: row;
      gap: 2.5rem;
    }

    a {
      padding: 0;
      font-size: 0.975rem;
      color: var(--muted);
    }

    a[aria-current="page"],
    a:hover {
      color: var(--fg);
    }
  }
`

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`

const ThemeButton = styled.button`
  height: 2.75rem;
  padding: 0 0.85rem;
  border: 0;
  border-radius: var(--radius-md);
  background: transparent;
  color: var(--fg);
  font-size: 0.875rem;
`

const NavIcon = styled.button`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 2.75rem;
  height: 2.75rem;
  padding: 0.85rem 0.7rem;
  border: 0;
  background: transparent;

  span {
    display: block;
    height: 2px;
    background: var(--fg);
    border-radius: 2px;
    transition: transform 200ms ease, opacity 200ms ease;
  }

  span:nth-child(1) {
    transform: ${props => (props.$open ? "translateY(7px) rotate(45deg)" : "none")};
  }
  span:nth-child(2) {
    opacity: ${props => (props.$open ? 0 : 1)};
  }
  span:nth-child(3) {
    transform: ${props => (props.$open ? "translateY(-7px) rotate(-45deg)" : "none")};
  }

  @media ${props => props.theme.breakpoints.tablet} {
    display: none;
  }
`

const Scrim = styled.div`
  position: fixed;
  inset: var(--header-h) 0 0;
  background: color-mix(in srgb, var(--bg) 70%, transparent);
  z-index: 30;

  @media ${props => props.theme.breakpoints.tablet} {
    display: none;
  }
`
